import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import cn from 'classnames';

import { useHttp } from 'hooks/http.hook';
import { showErrorMessage, showSuccessMessage } from 'helpers/notifications';
import { CHAT_CONVERSATION } from 'endpointsConst';

import StarsRateComponent from 'components/common/StarsRateComponent';
import { MAX_RATE_COUNT } from 'constants/settings';

import styles from './styles.module.scss';

const PAYMENT_MARKS = {
    NOT_PAID: 1,
    DELAYED: 2,
    ON_TIME: 3,
};

const ReviewMessage = ({ message }) => {
    const { request } = useHttp();
    const { t } = useTranslation();

    // Initialize state from message data
    const [rating, setRating] = useState(message.item.mark || 0);
    const [paymentMark, setPaymentMark] = useState(message.item.payment_mark || null);
    const [comment, setComment] = useState(message.item.comment || '');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Check if review has been submitted
    const hasReview = message.item.mark > 0;

    const handleSubmitReview = useCallback(
        (e) => {
            e.preventDefault();

            if (!rating) {
                showErrorMessage(t('dialog.chooseRate'));
                return;
            }

            setIsSubmitting(true);

            const payload = {
                id: message.id,
                mark: rating,
            };

            if (paymentMark) {
                payload.payment_mark = paymentMark;
            }

            if (comment) {
                payload.comment = comment;
            }

            request(CHAT_CONVERSATION + '/review', 'post', payload)
                .then(() => {
                    showSuccessMessage(t('dialog.rateSent'));
                    // Update message data to reflect submitted review
                    message.item.mark = rating;
                    if (message.item.is_carrier) {
                        message.item.payment_mark = paymentMark;
                    }
                    message.item.comment = comment;
                })
                .catch((e) => {
                    showErrorMessage(t('dialog.cantRate'));
                })
                .finally(() => {
                    setIsSubmitting(false);
                });
        },
        [rating, paymentMark, comment, request, t, message],
    );

    const handleStarsCountChange = useCallback((mark) => {
        setRating(mark);
    }, []);

    const getPaymentLabel = useCallback(
        (mark) => {
            switch (mark) {
                case PAYMENT_MARKS.ON_TIME:
                    return t('dialog.review.paymentOnTime');
                case PAYMENT_MARKS.DELAYED:
                    return t('dialog.review.paymentDelayed');
                case PAYMENT_MARKS.NOT_PAID:
                    return t('dialog.review.paymentNotPaid');
                default:
                    return '';
            }
        },
        [t],
    );

    // View mode - when review is already submitted
    if (hasReview) {
        const paymentMarkNumber = +message.item.payment_mark;
        return (
            <div className={styles.review}>
                <div className={styles.review__title}>{t('dialog.review.rate')}</div>
                <StarsRateComponent value={message.item.mark} maxRate={MAX_RATE_COUNT} />
                {message.item.payment_mark && (
                    <div className={styles.review__payment}>
                        <div className={styles.review__paymentLabel}>{t('dialog.review.paymentLabel')}</div>
                        <div
                            className={cn(
                                styles.review__paymentBadge,
                                paymentMarkNumber === PAYMENT_MARKS.ON_TIME && styles.review__paymentBadge_onTime,
                                paymentMarkNumber === PAYMENT_MARKS.DELAYED && styles.review__paymentBadge_delayed,
                                paymentMarkNumber === PAYMENT_MARKS.NOT_PAID && styles.review__paymentBadge_notPaid,
                            )}
                        >
                            {getPaymentLabel(paymentMarkNumber)}
                        </div>
                    </div>
                )}
                {message.item.comment && <div className={styles.review__text}>{message.item.comment}</div>}
            </div>
        );
    }

    // Write mode - when user is creating a review
    return (
        <form className={styles.review} onSubmit={handleSubmitReview}>
            <div className={styles.review__title}>{t('dialog.review.send')}</div>
            <StarsRateComponent onStarsCountChange={handleStarsCountChange} value={rating} maxRate={MAX_RATE_COUNT} />

            {message.item.is_carrier && (
                <>
                    <div className={styles.review__paymentQuestion}>{t('dialog.review.payment')}</div>

                    {
                        <div className={styles.review__paymentOptions}>
                            <button
                                type="button"
                                className={cn(
                                    styles.review__paymentOption,
                                    styles.review__paymentOption_onTime,
                                    paymentMark === PAYMENT_MARKS.ON_TIME && styles.review__paymentOption_selected,
                                )}
                                onClick={() => setPaymentMark(PAYMENT_MARKS.ON_TIME)}
                            >
                                {t('dialog.review.paymentOnTime')}
                            </button>
                            <button
                                type="button"
                                className={cn(
                                    styles.review__paymentOption,
                                    styles.review__paymentOption_delayed,
                                    paymentMark === PAYMENT_MARKS.DELAYED && styles.review__paymentOption_selected,
                                )}
                                onClick={() => setPaymentMark(PAYMENT_MARKS.DELAYED)}
                            >
                                {t('dialog.review.paymentDelayed')}
                            </button>
                            <button
                                type="button"
                                className={cn(
                                    styles.review__paymentOption,
                                    styles.review__paymentOption_notPaid,
                                    paymentMark === PAYMENT_MARKS.NOT_PAID && styles.review__paymentOption_selected,
                                )}
                                onClick={() => setPaymentMark(PAYMENT_MARKS.NOT_PAID)}
                            >
                                {t('dialog.review.paymentNotPaid')}
                            </button>
                        </div>
                    }
                </>
            )}

            <textarea
                className={styles.review__textarea}
                placeholder={t('dialog.review.textPlaceholder')}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
            />

            <button type="submit" className={styles.review__submitButton} disabled={!rating || isSubmitting}>
                {t('dialog.review.sendButton')}
            </button>
        </form>
    );
};

ReviewMessage.propTypes = {
    message: PropTypes.shape({
        id: PropTypes.number,
        item: PropTypes.shape({
            mark: PropTypes.number,
            payment_mark: PropTypes.number,
            comment: PropTypes.string,
            is_carrier: PropTypes.bool,
        }),
    }).isRequired,
};

export default ReviewMessage;
